import {EventEmitter, Injectable} from '@angular/core';
import {ObjectFactory}            from 'ts-tooling';
import {PlantProtectionPlanBookStore} from '../../stores/plant-protection/plant-protection-planbook.store';
import {FieldStore}                   from '../../stores/farm/field.store';
import {MapStore}                     from '../../stores/map/map.store';
import {ApShowPointsOnMapService}     from '../../ap-utils/service/ap-show-points-on-map.service';
import {FormStore}                    from '../../stores/layout/form.store';
import {BehaviorSubject}              from 'rxjs';
import {
  PpLogfileEditComponent
}                                     from '../../entry-components/plant-protection-entry-components/pp-logfile-edit.component';
import {IPpLogfileGridItem}           from '../types/pp-logfile.types';
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import IPlantProtectionSensorLogHeaderData = Data.Import.IPlantProtectionSensorLogHeaderData;
import {AgriportConstantsService}     from '../../services/common/agriport-constants.service';

@Injectable({
  providedIn: 'root'
})
export class PpLogfileService {
  public saving = new BehaviorSubject<boolean>(false);
  public onClearSelection = new EventEmitter();

  constructor(private store: PlantProtectionPlanBookStore,
              private fieldStore: FieldStore,
              private formStore: FormStore,
              private mapStore: MapStore,
              private showPointsOnMapService: ApShowPointsOnMapService) {
    this.store.Listen((s) => s.logfiles).subscribe(() => {
      this.saving.next(false);
    });
  }

  public logBookingReady(item: IDatasetFieldMap): boolean {
    return item?.PlantProtectionPlanBook?.ProductList.length > 0 && item?.Ec > 9 && item?.Ec < 70 && item?.WorkingWidth >= 10 && item?.WorkingWidth <= 100;
  }

  public logIsRejected(item: IDatasetFieldMap): boolean {
    return item?.Status === -1;
  }

  public logBooked(item: IDatasetFieldMap): boolean {
    return item?.Status === 1 && item?.PlantProtectionPlanBook?.BookedBy != null;
  }

  public onAccept(dataItem: IPpLogfileGridItem | IPpLogfileGridItem[]): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    this._hidePoints(dataItem);
    this.saving.next(true);
    this.store.bookPlanBooks(dataItem.map((item) =>
      ({planBook: item.SourceItem.PlantProtectionPlanBook, logFile: item.SourceItem}))
    );
  }

  public onReset(dataItem: any | any[]): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    this._hidePoints(dataItem);
    this.saving.next(true);
    this.store.unBookPlanBooks(
      this._getLogfiles(dataItem).map((log) =>
        ({planBook: log.PlantProtectionPlanBook, logFile: log}))
    );
  }

  public onReject(dataItem: any): void {
    if (!Array.isArray(dataItem)) {
      dataItem = [dataItem];
    }
    this._hidePoints(dataItem);
    const logFiles = this._getLogfiles(dataItem).map((log) => {
      log = ObjectFactory.Copy(log);
      log.Status = -1;
      const areLogfilesValid = this.store.getLogfilesByPlanBookId(log.PlantProtectionPlanBook?.Id)
        .filter(l => l.Id !== log.Id)
        .every(l => l.Status < 1);
      if (!!log?.PlantProtectionPlanBook?.BookedBy && areLogfilesValid) {
        this.store.unBookPlanBooks([{planBook: log.PlantProtectionPlanBook, logFile: log}]);
        return null;
      }
      log.PlantProtectionPlanBook = null;
      return log;
    }).filter(log => !!log);
    this.saving.next(true);
    this.store.updateLogfiles(logFiles);
  }

  public onRemap(dataItem: IPpLogfileGridItem | IPpLogfileGridItem[]): void {
    this._hidePoints(dataItem);
    this.saving.next(true);
    if (Array.isArray(dataItem)) {
      this.store.remapLogfile(dataItem.map((item) => item.SourceItem));
    } else {
      this.store.remapLogfile([dataItem.SourceItem]);
    }
  }

  public logFileValid(item: IDatasetFieldMap): boolean {
    const json = JSON.parse(item?.DataSet?.HeaderValuesString);
    return !!this.fieldStore.getFieldByFieldGeomId(item?.FieldGeomId) &&
      !!item.CropType && !!json['CropType'] &&
      item.Ec != null && json['EcStage'] != null &&
      item.ApplicationMode != null && json['ApplicationMode'] != null;
  }

  public onEdit(dataItem: any): void {
    this.formStore.openForm({component: PpLogfileEditComponent, data: dataItem});
  }

  public toggleLogPointsOnMap(selectedItems: IPpLogfileGridItem[], farmId: number): void {
    let selectedMin = Number.MAX_SAFE_INTEGER;
    let selectedMax = 0;
    let showLegend = true;

    if (selectedItems && selectedItems?.length > 0) {
      for (const d of selectedItems) {
        // check min and max of the other selected items and adjust min/max boundaries
        if (d?.SourceItem?.NUptakeMin < selectedMin) {
          selectedMin = d?.SourceItem?.NUptakeMin;
        }
        if (d?.SourceItem?.NUptakeMax > selectedMax) {
          selectedMax = d?.SourceItem?.NUptakeMax;
        }
      }
    }

    // if there is no or invalid data we hide the legend
    if (selectedMin === Number.MAX_SAFE_INTEGER || !isFinite(selectedMax) || !isFinite(selectedMin)) {
      showLegend = false;
    }

    // finally update legend values/titles/colors and reload map considering
    // the new min/max boundaries and the corresponding changed colors.
    // To make this work properly the MapFactoryStyler cache needs to be cleared
    // everytime the min/max is recalculated
    this.mapStore.Legends.LogLegend.updateValues(selectedMin, selectedMax);
    this.mapStore.Legends.refreshLegend(this.mapStore.Legends.LogLegend, showLegend);
    this.showPointsOnMapService.showPoints(selectedItems?.map(d => {
      return {
        DatasetId: d.SourceItem.DataSet.Id as string,
        FieldGeomId: d.SourceItem.FieldGeomId as string
      };
    }), farmId);
  }

  public getPlanning(dataItem: IDatasetFieldMap): IPlantProtectionPlanBook {
    const headerValues: IPlantProtectionSensorLogHeaderData = JSON.parse(dataItem.DataSet.HeaderValuesString);
    const planBooks = this.store.Listen(s => s.data).getValue();
    const sorted = planBooks
      .filter((pb) => {
        return pb.DeletedBy == null &&
          pb.FieldGeomId === dataItem.FieldGeomId &&
          pb.LogfileOperationMode === +headerValues.OperationMode &&
          pb.ProductList.Any(
            (product) => headerValues.Agents.Any(
              (agent) => product.Product?.ExternalProductId === agent.Id)) &&
          Math.abs(new Date(pb.ApplicationDate).valueOf() - new Date(dataItem.DataSet.Created).valueOf()) <
          +AgriportConstantsService.GetConstant('PLAN_DATE_DELTA') * 24 * 60 * 60 * 1000;
      })
      .sort((pb) =>
        pb.ProductList.FindAll(pl => headerValues.Agents.Find(agent => agent.Id === pl.Product.ExternalProductId) != null).length
      );
    return sorted.FirstOrDefault();
  }

  private _hidePoints(dataItem: IPpLogfileGridItem | IPpLogfileGridItem[]): void {
    this.onClearSelection.emit();
  }

  private _getLogfiles(items: any[]): IDatasetFieldMap[] {
    const ids = items.map((item) => item['Id']);
    return this.store.Listen((s) => s.logfiles).getValue()
      .filter((log) => ids.some((id) => id === log.Id.toString()))
      .map((log) => {
        log = ObjectFactory.Copy(log);
        log.PlantProtectionPlanBook = items.find((item) => item['Id'] === log.Id)?.SourceItem.PlantProtectionPlanBook;
        return log;
      });
  }
}
